import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent, ClipboardEvent } from 'react';
import { fileToDataUrl, fetchImageAsDataUrl } from '../utils/images';

export type ImageAssetSource = 'upload' | 'url' | 'clipboard';

export interface ImageAsset {
  dataUrl: string;
  name: string;
  source: ImageAssetSource;
  link?: string;
}

interface ImagePickerProps {
  value: ImageAsset | null;
  onChange: (value: ImageAsset | null) => void;
}

function normalizeLink(raw: string): string | undefined {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const hasScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(trimmed);
  return hasScheme ? trimmed : `https://${trimmed}`;
}

export function ImagePicker({ value, onChange }: ImagePickerProps) {
  const [urlInput, setUrlInput] = useState('');
  const [linkInput, setLinkInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    setLinkInput(value?.link ?? '');
  }, [value?.link]);

  const handleFile = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      try {
        setError(null);
        const dataUrl = await fileToDataUrl(file);
        onChange({
          dataUrl,
          name: file.name,
          source: 'upload',
          link: normalizeLink(linkInput),
        });
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : 'Unable to read the selected image.';
        setError(message);
      } finally {
        event.target.value = '';
      }
    },
    [linkInput, onChange],
  );

  const handleUrlImport = useCallback(async () => {
    if (!urlInput.trim()) {
      setError('Enter an image URL before loading.');
      return;
    }
    try {
      setIsFetching(true);
      setError(null);
      const dataUrl = await fetchImageAsDataUrl(urlInput.trim());
      onChange({
        dataUrl,
        name: urlInput.trim(),
        source: 'url',
        link: normalizeLink(linkInput),
      });
    } catch (cause) {
      const message =
        cause instanceof Error ? cause.message : 'Unable to fetch the image from the provided URL.';
      setError(message);
    } finally {
      setIsFetching(false);
    }
  }, [linkInput, onChange, urlInput]);

  const handlePaste = useCallback(
    async (event: ClipboardEvent<HTMLDivElement>) => {
      const item = Array.from(event.clipboardData.files).find((file) => file.type.startsWith('image/'));
      if (!item) return;
      event.preventDefault();
      try {
        setError(null);
        const dataUrl = await fileToDataUrl(item);
        onChange({
          dataUrl,
          name: item.name || 'pasted-image.png',
          source: 'clipboard',
          link: normalizeLink(linkInput),
        });
      } catch (cause) {
        const message = cause instanceof Error ? cause.message : 'Unable to process pasted image.';
        setError(message);
      }
    },
    [linkInput, onChange],
  );

  const handleLinkChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const nextLink = event.target.value;
      setLinkInput(nextLink);
      if (value) {
        onChange({ ...value, link: normalizeLink(nextLink) });
      }
    },
    [onChange, value],
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setUrlInput('');
    setLinkInput('');
    setError(null);
  }, [onChange]);

  return (
    <div className="space-y-4">
      <div
        className="section-card flex flex-col gap-4 border-dashed border-slate-200 bg-slate-50/60"
        onPaste={handlePaste}
      >
        <div className="flex flex-col gap-3 md:flex-row md:items-end">
          <div className="flex-1 space-y-2">
            <label className="form-label" htmlFor="image-upload">
              Upload image
            </label>
            <label className="flex h-32 cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white text-sm text-slate-500 transition hover:border-brand-400 hover:text-brand-600">
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFile}
              />
              <span className="text-xs uppercase tracking-widest text-brand-600">Click to browse</span>
              <span className="text-xs text-slate-400">or paste directly into this box</span>
            </label>
          </div>
          <div className="flex-1 space-y-2">
            <label className="form-label" htmlFor="image-url">
              Image URL
            </label>
            <div className="flex gap-2">
              <input
                id="image-url"
                type="url"
                placeholder="https://example.com/creative-visual.png"
                className="input-base flex-1"
                value={urlInput}
                onChange={(event) => setUrlInput(event.target.value)}
              />
              <button type="button" className="secondary-button whitespace-nowrap" onClick={handleUrlImport} disabled={isFetching}>
                {isFetching ? 'Loading…' : 'Load'}
              </button>
            </div>
            <p className="text-xs text-slate-500">
              Supports drag & drop, clipboard paste, direct upload, or fetching from an image URL.
            </p>
            <div className="space-y-2">
              <label className="form-label" htmlFor="image-link">
                Image link (optional)
              </label>
              <input
                id="image-link"
                type="url"
                placeholder="https://example.com/learn-more"
                className="input-base"
                value={linkInput}
                onChange={handleLinkChange}
              />
              <p className="text-xs text-slate-400">
                When provided, clicking the preview image or generated PDF will open this link in a new tab.
              </p>
            </div>
          </div>
        </div>

        {error ? <p className="rounded-xl bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p> : null}

        {value ? (
          <div className="flex flex-col gap-3 rounded-2xl border border-brand-100 bg-white p-4 shadow-soft">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-700">{value.name}</p>
                <p className="text-xs capitalize text-slate-400">Source: {value.source}</p>
                {value.link ? (
                  <a
                    href={value.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-brand-600 underline"
                  >
                    Open linked page
                  </a>
                ) : null}
              </div>
              <button type="button" className="secondary-button" onClick={handleClear}>
                Remove
              </button>
            </div>
            {value.link ? (
              <a href={value.link} target="_blank" rel="noopener noreferrer" className="block">
                <img src={value.dataUrl} alt={value.name} className="max-h-60 w-full rounded-2xl object-cover" />
              </a>
            ) : (
              <img src={value.dataUrl} alt={value.name} className="max-h-60 w-full rounded-2xl object-cover" />
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
